const languageContext = (form, submitter) => ({
  current: document.documentElement.lang.toLowerCase(),
  target: (submitter?.lang || form.querySelector('input[name="language_code"]')?.value ||
    form.querySelector('button[lang]')?.lang || '').toLowerCase(),
});

const updateLanguageReturnTo = (form, submitter, validatedFacets, sourceFacets) => {
  if (!form?.classList.contains('LanguageSwitcherForm')) return;
  const returnTo = form.querySelector('input[name="return_to"]');
  if (!returnTo) return;
  const destination = new URL(window.location.href);
  const { current, target } = languageContext(form, submitter);
  const activePreview = document.querySelector(
    '[data-theme-environment-switcher] [data-theme-preview-id][aria-current="true"]'
  );
  let resetPagination = false;

  if (current && target && current !== target && destination.searchParams.has('filter.p.product_type')) {
    // An explicit empty value also overrides Shopify's incoming query preservation.
    destination.searchParams.set('filter.p.product_type', '');
    resetPagination = true;
  }
  validatedFacets?.forEach((values, name) => {
    const original = sourceFacets?.get(name) || destination.searchParams.getAll(name);
    destination.searchParams.delete(name);
    if (values.length) values.forEach(value => destination.searchParams.append(name, value));
    else destination.searchParams.set(name, '');
    if (JSON.stringify(original) !== JSON.stringify(values)) resetPagination = true;
  });
  if (resetPagination) {
    destination.searchParams.set('page', '1');
    destination.searchParams.set('finish_page', '1');
  }

  // Keep repeated values exact, including a single value containing a literal comma.
  const repeatedFacets = [...new Set(destination.searchParams.keys())]
    .filter(name => name.startsWith('filter.') && !name.startsWith('filter.v.price.'))
    .map(name => [name, [...new Set(destination.searchParams.getAll(name))]])
    .filter(([, values]) => values.length > 1);
  destination.searchParams.delete('_berries_filter_values');
  if (repeatedFacets.length) {
    destination.searchParams.set('_berries_filter_values', JSON.stringify(repeatedFacets));
  }
  if (activePreview) destination.searchParams.set('preview_theme_id', activePreview.dataset.themePreviewId);
  returnTo.value = destination.pathname + destination.search + destination.hash;
};

const languageDependentFacets = () => {
  const destination = new URL(window.location.href);
  const fields = Array.from(document.querySelectorAll('#collection-filters-drawer-form input[name]'));
  const selection = new Map();
  for (const name of new Set(destination.searchParams.keys())) {
    if (!name.startsWith('filter.') || name.startsWith('filter.v.price.') ||
      ['filter.v.availability', 'filter.p.product_type'].includes(name)) continue;
    const checked = fields.filter(field => field.name === name && field.checked).map(field => field.value);
    const values = checked.length ? checked : destination.searchParams.getAll(name).filter(Boolean);
    if (values.length) selection.set(name, [...new Set(values)]);
  }
  return selection;
};

const validateLanguageFacets = async (selection, targetLanguage) => {
  const alternate = Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]'))
    .find(link => link.hreflang.toLowerCase() === targetLanguage);
  if (!alternate) return null;
  const destination = new URL(alternate.href, window.location.href);
  if (destination.origin !== window.location.origin) return null;
  destination.hash = '';
  destination.search = '';
  const current = new URL(window.location.href);
  // Search results need the same query and scope, without language-dependent filters.
  for (const name of ['q', 'type', 'options[prefix]']) {
    if (current.searchParams.has(name)) destination.searchParams.set(name, current.searchParams.get(name));
  }
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 4000);
  try {
    // The selected preview is already in the session. Adding its URL parameter here
    // would introduce a Shopify redirect which can collapse structured parameters.
    const response = await fetch(destination.href, { credentials: 'same-origin', signal: controller.signal });
    if (!response.ok) return null;
    const target = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (target.documentElement.lang.toLowerCase() !== targetLanguage) return null;
    const section = target.querySelector('[data-section-type="collection"]');
    if (!section) return null;
    const renderedTheme = section.dataset.renderedThemeId || section.dataset.finishTheme;
    const activeTheme = window.Shopify?.theme?.id;
    if (activeTheme && String(renderedTheme) !== String(activeTheme)) return null;
    const options = Array.from(target.querySelectorAll('#collection-filters-drawer-form input[name]'));
    return new Map([...selection].map(([name, values]) => {
      const available = new Set(options.filter(field => field.name === name).map(field => field.value));
      return [name, values.filter(value => available.has(value))];
    }));
  } catch {
    // Unavailable validation must not be interpreted as an absent option.
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
};

const setLanguageSwitchError = (form, visible) => {
  const wrapper = form.closest('[data-language-switcher]');
  const feedback = wrapper?.querySelector('[data-language-switch-error]');
  if (!feedback) return;
  const message = feedback.querySelector('.UiFeedback__message') || feedback;
  message.textContent = visible ? feedback.dataset.languageSwitchMessage : '';
  feedback.hidden = !visible;
};

let languageSwitchPending = false;
const restoreLanguageButtons = () => {
  document.querySelectorAll('[data-language-filter-pending]').forEach(button => {
    button.disabled = false;
    delete button.dataset.languageFilterPending;
  });
  document.querySelectorAll('.LanguageSwitcherForm[aria-busy="true"]').forEach(form => {
    form.removeAttribute('aria-busy');
  });
};

const prepareLanguageSwitch = (event, form, submitter) => {
  if (!form?.classList.contains('LanguageSwitcherForm')) return;
  if (form.dataset.languageFilterValidated === 'true') return;
  if (languageSwitchPending) {
    event.preventDefault();
    return;
  }
  setLanguageSwitchError(form, false);
  const returnTo = form.querySelector('input[name="return_to"]');
  const previousReturnTo = returnTo?.value;
  updateLanguageReturnTo(form, submitter);
  const { current, target } = languageContext(form, submitter);
  const selection = languageDependentFacets();
  if (!target || current === target || !selection.size) return;
  event.preventDefault();
  languageSwitchPending = true;
  form.setAttribute('aria-busy', 'true');
  document.querySelectorAll('.LanguageSwitcherForm button[type="submit"]').forEach(button => {
    if (!button.disabled) {
      button.dataset.languageFilterPending = 'true';
      button.disabled = true;
    }
  });
  const validationUnavailable = () => {
    if (returnTo) returnTo.value = previousReturnTo;
    restoreLanguageButtons();
    setLanguageSwitchError(form, true);
    // Disabling a focused submit button can move focus to the document body.
    // Return it to the same action so keyboard users can retry naturally.
    submitter?.focus({ preventScroll: true });
  };
  validateLanguageFacets(selection, target).then(validated => {
    if (validated === null) {
      validationUnavailable();
      return;
    }
    updateLanguageReturnTo(form, submitter, validated, selection);
    restoreLanguageButtons();
    form.dataset.languageFilterValidated = 'true';
    // Keep the native submit event for other listeners and Shopify localization.
    HTMLFormElement.prototype.requestSubmit.call(form, submitter || undefined);
  }).catch(validationUnavailable).finally(() => {
    delete form.dataset.languageFilterValidated;
    languageSwitchPending = false;
    restoreLanguageButtons();
  });
};

document.addEventListener('click', event => {
  const button = event.target instanceof Element
    ? event.target.closest('.LanguageSwitcherForm button[type="submit"]') : null;
  if (button) prepareLanguageSwitch(event, button.form, button);
}, true);

document.addEventListener('submit', event => {
  prepareLanguageSwitch(event, event.target, event.submitter);
}, true);

window.addEventListener('pageshow', () => {
  languageSwitchPending = false;
  restoreLanguageButtons();
});
